import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import {
  AppNativeEventsService,
  AppRoutes,
  FeatureFlagsService,
  NavigationService,
  QuickStartSheetService,
  TranslationService
} from '@core';
import { SessionSyncService } from '@session';
import { SoftUpdateNudgeService, VersionConfigSyncService } from '@update';
import { SzTabBarItem } from '@ui';

@Component({
  selector: 'app-component',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent {
  readonly navVisible: WritableSignal<boolean> = signal<boolean>(true);
  readonly helloMessage: WritableSignal<string> = signal<string>('loading...');

  private splashHidden: boolean = false;
  private splashNavReady: boolean = false;
  private splashMinTimeElapsed: boolean = false;
  private readonly SPLASH_MIN_MS: number = 2000;
  private readonly SPLASH_MAX_MS: number = 10000;

  readonly tabItems: ReadonlyArray<SzTabBarItem> = [
    { label: 'Today', icon: 'home', route: `/${AppRoutes.TODAY}` },
    { label: 'Drills', icon: 'target', route: `/${AppRoutes.DRILLS}` },
    { label: 'Plan', icon: 'calendar', route: `/${AppRoutes.PLAN}` },
    { label: 'Progress', icon: 'chart', route: `/${AppRoutes.PROGRESS}` }
  ];

  private readonly router: Router = inject(Router);
  private readonly navigation: NavigationService = inject(NavigationService);
  readonly quickStart: QuickStartSheetService = inject(QuickStartSheetService);
  private readonly sessionSync: SessionSyncService = inject(SessionSyncService);
  private readonly appNativeEvents: AppNativeEventsService = inject(AppNativeEventsService);
  private readonly translation: TranslationService = inject(TranslationService);
  private readonly versionConfigSync: VersionConfigSyncService = inject(VersionConfigSyncService);
  private readonly softUpdateNudge: SoftUpdateNudgeService = inject(SoftUpdateNudgeService);
  private readonly featureFlags: FeatureFlagsService = inject(FeatureFlagsService);

  constructor() {
    this.navigation.init();
    this.appNativeEvents.init();
    this.versionConfigSync.startSync();
    this.softUpdateNudge.maybeShow();
    this.featureFlags.start();
    this.applyStatusBarTheme();
    this.showSplashOnBoot();
    this.subscribeToRouteChanges();
    this.sessionSync.attemptResume();
    this.maybeShowLanguagePicker();
  }

  private showSplashOnBoot(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    void SplashScreen.show({ autoHide: false });
    setTimeout(() => {
      this.splashMinTimeElapsed = true;
      this.maybeHideSplash();
    }, this.SPLASH_MIN_MS);
    setTimeout(() => this.forceHideSplash(), this.SPLASH_MAX_MS);
  }

  private hideSplashOnce(): void {
    this.splashNavReady = true;
    this.maybeHideSplash();
  }

  private maybeHideSplash(): void {
    if (this.splashHidden || !Capacitor.isNativePlatform()) {
      return;
    }
    if (!this.splashNavReady || !this.splashMinTimeElapsed) {
      return;
    }
    this.splashHidden = true;
    void SplashScreen.hide();
  }

  private forceHideSplash(): void {
    if (this.splashHidden || !Capacitor.isNativePlatform()) {
      return;
    }
    this.splashHidden = true;
    void SplashScreen.hide();
  }

  private maybeShowLanguagePicker(): void {
    if (this.translation.hasUserPickedLanguage()) return;
    void this.router.navigateByUrl(`/${AppRoutes.LANG}`);
  }

  onFabTap(): void {
    if (this.quickStart.open()) {
      this.quickStart.hide();
    } else {
      this.quickStart.show();
    }
  }

  private applyStatusBarTheme(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    void StatusBar.setStyle({ style: Style.Dark });
    void StatusBar.setBackgroundColor({ color: '#0c0d10' });
  }

  private subscribeToRouteChanges(): void {
    this.router.events
      .pipe(
        filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e: NavigationEnd) => {
        const url: string = e.urlAfterRedirects;
        this.navVisible.set(!this.isTabBarHiddenRoute(url));
        this.hideSplashOnce();
      });
  }

  private isTabBarHiddenRoute(url: string): boolean {
    // Podsumowanie sesji otwarte z historii (bez completed=true) pokazuje dolny navbar
    // jak inne podglądy; po zakończeniu sesji (completed=true) navbar ukryty - zostaje CTA „Gotowe".
    if (url.startsWith('/session/summary') && !url.includes('completed=true')) {
      return false;
    }
    if (
      url.startsWith('/auth') ||
      url.startsWith('/lang') ||
      url.startsWith('/legal') ||
      url.startsWith('/profile') ||
      url.startsWith('/partners') ||
      url.startsWith('/admin') ||
      url.startsWith('/session') ||
      url.startsWith('/playground') ||
      url.startsWith('/blocked-version')
    )
      return true;
    const detailLikeSegments: readonly string[] = ['/detail', '/create', '/edit'];
    return detailLikeSegments.some((seg: string): boolean => url.includes(seg));
  }
}
